import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { environment } from '@env/environment';
import { defer } from 'rxjs';
import { filter, map, startWith, take, tap } from 'rxjs/operators';
import { AuthenticationService } from '@auth-n/authentication.service';
import { isInvitationPath } from '@security/user-invitations/user-invitation-routes';
import { DOMAIN_MOVED_ROUTE } from '@domain/domain-migration-landing.guard';

declare var window: any;

/**
 * Nothing more than a router outlet for the {@link: AppRoutingModule}.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent {
  activeIdentity$ = this.activeAuthentication.activeIdentity$;

  // do not load app-new-layout when route is under-maintenance
  isMaintenanceRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.url.includes('/under-maintenance')),
    startWith(window.location.pathname.includes('/under-maintenance'))
  );

  // do not load app-new-layout when route is an error page
  isErrorPageRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      const errorRoutes = ['/no-permissions', '/forbidden', '/locked'];
      return errorRoutes.some((route) => event.url.includes(route));
    }),
    startWith(false)
  );

  // do not load app-new-layout on an invitation link - a signed-in user accepting an invitation may have no memberships yet
  isInvitationRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => isInvitationPath(event.url)),
    startWith(isInvitationPath(window.location.pathname))
  );

  // render the default outlet without requiring activeIdentity for /logout
  isLogoutRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.url === '/logout'),
    startWith(window.location.pathname === '/logout')
  );

  // render the default outlet, without the signed-in layout or an active identity, for the retiring domain's "we've moved" page.
  // Deferred so every subscription starts from the URL as it is *now*. The template subscribes more than once, and a branch
  // created later (when the identity arrives after the redirect) would otherwise start from the URL at construction time --
  // before the redirect to this page -- and briefly render the signed-in layout over it.
  isDomainMovedRoute$ = defer(() =>
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      startWith(this.router.navigated ? this.router.url : window.location.pathname),
      map((url) => url.startsWith(DOMAIN_MOVED_ROUTE))
    )
  );

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private activeAuthentication: ActiveAuthenticationStateService,
    private authenticationService: AuthenticationService
  ) {
    if (environment.testSupportEnabled) this.createTestSupport(this.ngZone, this.router);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        take(1)
      )
      .subscribe((event: NavigationStart) => {
        this.authenticationService.setInitialUrl(event.url);
      });

    this.reattachBareRoutesAfterLayoutSwitch();
  }

  /**
   * When on /under-maintenance or the domain-moved landing page while authenticated, the template
   * switches from <app-new-layout> (which owns the primary <router-outlet>) to a bare
   * <router-outlet>. This destroys the outlet the router already attached the component to.
   * Re-navigate once per route on the next tick so the new outlet picks up the route.
   *
   * Two things can cause the switch, so both are watched: a navigation that ends on the route while
   * the identity is already set, and the identity arriving *after* the route attached (a fresh visit,
   * where the redirect can land before the session is restored). Without the second, the latter
   * leaves a blank page.
   */
  private reattachBareRoutesAfterLayoutSwitch(): void {
    const bareRoutes = ['/under-maintenance', DOMAIN_MOVED_ROUTE];
    const reattached = new Set<string>();
    const reattachIfBare = (url: string) => {
      const route = bareRoutes.find((bareRoute) => url.includes(bareRoute));
      if (route && this.activeAuthentication.activeIdentity && !reattached.has(route)) {
        reattached.add(route);
        setTimeout(() => this.router.navigateByUrl(route).then());
      }
    };

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => reattachIfBare(event.url));
    this.activeAuthentication.activeIdentity$.pipe(filter((identity) => !!identity)).subscribe(() => reattachIfBare(this.router.url));
  }

  private createTestSupport(ngZone: NgZone, router: Router) {
    // see also: e2e/cypress/support/ng-route-to.ts
    window.ngNavigateByUrl = function (url: string) {
      return new Promise<boolean>((resolve, reject) => {
        ngZone.run(() => {
          router.navigateByUrl(url).then(resolve, reject);
        });
      });
    };

    Object.defineProperty(document, 'referrer', {
      get() {
        return 'https://hr.test.cintra.cloud';
      }
    });
  }
}
